import type { ReactNode } from "react";
import { CircleAlert, RotateCcw, X } from "lucide-react";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/** The one action that fixes the cause, e.g. "Sign in to the repos". */
export interface ErrorLineFix {
  label: string;
  onClick: () => void;
}

export interface ErrorLineProps {
  /** What happened, bold: "The redraft didn't finish." */
  title: ReactNode;
  /** Why, and what did or did not change: "Claude couldn't read … Nothing changed: your 7 areas are as they were." */
  cause: ReactNode;
  /** Optional primary button that fixes the cause. */
  fix?: ErrorLineFix;
  /** Shows "Try again" when given. */
  onRetry?: () => void;
  /** The × button. */
  onClose: () => void;
  className?: string;
}

/**
 * An error that stays on screen until it is dealt with (epic PI-SPEC-SW-E294,
 * feature PI-SPEC-SW-1508, UX-PM-16) — design class `.errline`, frame F3 in
 * Claude Design "PM Packages B E F Mockups".
 *
 * It has no timer and never dismisses itself. The page renders it while it
 * holds an error and clears that error from the three ways out: the fix
 * button, "Try again" and ×. Each calls its callback; the parent removes the
 * line (and shows a new one if the retry fails again).
 */
export function ErrorLine({ title, cause, fix, onRetry, onClose, className }: ErrorLineProps) {
  return (
    <div
      role="alert"
      data-testid="error-line"
      className={cn(
        "flex items-center gap-3 rounded-[10px] border border-[rgba(225,24,55,0.4)] bg-[#150C0E] px-3.5 py-[11px] text-[#FAFAFA]",
        TOOLBOX_TEXT.content,
        className
      )}
    >
      <CircleAlert className="h-[18px] w-[18px] flex-none text-[#FF8A9B]" aria-hidden="true" />
      <p className="min-w-0 flex-1 leading-snug">
        <b className="font-semibold" data-testid="error-line-title">
          {title}
        </b>{" "}
        <span className="text-[#FBD5DB]" data-testid="error-line-cause">
          {cause}
        </span>
      </p>
      {fix ? (
        <button
          type="button"
          onClick={fix.onClick}
          data-testid="error-line-fix"
          className={cn(
            "inline-flex h-[30px] flex-none items-center gap-1.5 whitespace-nowrap rounded-lg bg-[#E11837] px-[13px] font-semibold text-white",
            "hover:bg-[#C8102E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8A9B]"
          )}
        >
          {fix.label}
        </button>
      ) : null}
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          data-testid="error-line-retry"
          className={cn(
            "inline-flex h-[30px] flex-none items-center gap-1.5 whitespace-nowrap rounded-lg border border-[#27272A] bg-[#1A1A1E] px-[11px] font-medium text-[#FAFAFA]",
            "hover:bg-[#27272A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8A9B]"
          )}
        >
          <RotateCcw className="h-4 w-4" aria-hidden="true" />
          Try again
        </button>
      ) : null}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        data-testid="error-line-close"
        className={cn(
          "inline-flex h-[30px] w-[30px] flex-none items-center justify-center rounded-lg text-[#A1A1AA]",
          "hover:bg-[#1A1A1E] hover:text-[#FAFAFA] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8A9B]"
        )}
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
